// import components
import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, TextField } from '@mui/material';
import {
  DesktopTimePicker,
  LocalizationProvider
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  ControlledPlanUnitSelector,
  ControlledTextField,
  Dialog
} from 'components/shared';
import {
  GridCompact12,
  GridCompact2,
  GridCompact4,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';

// import hooks
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

// import queries & mutations
import { mutations, queryKeys } from 'RestAPIs/rapini';

// import utils
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';

// import types
import type {
  AppointmentGroupingResponse,
  CreateAppointmentGroupingRequest,
  UpdateAppointmentGroupingRequest
} from 'generated';
import { InferType } from 'yup';

// import schemas
import addRouteSchema from './add-route-schema';
import { StyledCustomMediumDialogPaper } from 'components/shared/Paper/StyledPaper.styles';

const schema = addRouteSchema();
type Form = InferType<typeof schema>;

const start = dayjs().set('hour', 7).set('minute', 0);

const defaultValues = {
  code: '',
  description: '',
  start,
  endTime: '',
  minuteDuration: 0,
  hourDuration: 0,
  organizationalUnit: null
};

function convertHourMinutesToDayjs(hourMinutes: string) {
  const [hour, minutes] = hourMinutes
    .split(':')
    .map((str) => parseInt(str, 10));

  return dayjs().hour(hour).minute(minutes);
}

type Props = {
  open: boolean;
  handleClose: () => void;
  appointmentGroup?: AppointmentGroupingResponse;
};

export default function AddRouteDialog({
  open,
  handleClose,
  appointmentGroup
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: createAppointmentGrouping, isLoading } =
    mutations.useCreateAppointmentGrouping();
  const { mutate: updateAppointmentGrouping } =
    mutations.useUpdateAppointmentGrouping();

  const { control, handleSubmit, setValue, reset } = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  function getOrganizationalUnit(
    appointmentGroup: AppointmentGroupingResponse
  ) {
    if (!appointmentGroup?.organizationalUnit) return undefined;
    return {
      id: appointmentGroup.organizationalUnit?.id,
      code: appointmentGroup.organizationalUnit?.code,
      name: appointmentGroup.organizationalUnit?.name
    };
  }

  const [start, hourDur, minuteDur] = useWatch({
    control,
    name: ['start', 'hourDuration', 'minuteDuration']
  });

  const hourDuration = Number(hourDur) || 0;
  const minuteDuration = Number(minuteDur) || 0;

  const resetForm = () => {
    if (!appointmentGroup) {
      reset(defaultValues);
      return;
    }

    const ms = (appointmentGroup.durationInSeconds || 0) * 1000;

    reset({
      organizationalUnit: getOrganizationalUnit(appointmentGroup),
      code: appointmentGroup.code,
      description: appointmentGroup.description,
      start: convertHourMinutesToDayjs(appointmentGroup.start ?? '07:00'),
      endTime: convertHourMinutesToDayjs(
        appointmentGroup?.end ?? '07:00'
      ).format('HH:mm'),
      minuteDuration: dayjs(ms).get('m'),
      hourDuration: dayjs(ms).get('h') - 1 // 0 - 23, so subtracting 1
    });
  };

  useEffect(() => resetForm(), [appointmentGroup, open]);

  useEffect(() => {
    const endTime = start
      .add(hourDuration, 'hour')
      .add(minuteDuration, 'minute')
      .format('HH:mm');
    setValue('endTime', endTime);
  }, [start, minuteDuration, hourDuration]);

  async function onSubmit(data) {
    const {
      code,
      description,
      organizationalUnit,
      start,
      minuteDuration,
      hourDuration
    } = data;
    const organizationalUnitId = organizationalUnit?.id;
    const durationInSeconds = hourDuration * 3600 + minuteDuration * 60;

    if (appointmentGroup) {
      const payload: UpdateAppointmentGroupingRequest = {
        entityId: appointmentGroup.entityId,
        organizationalUnitId,
        code,
        description,
        start: start.format('HH:mm'),
        durationInSeconds
      };

      await updateAppointmentGrouping(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getAppointmentGroupings.name);
          handleClose();
          resetForm();
        }
      });
    } else {
      const payload: CreateAppointmentGroupingRequest = {
        organizationalUnitId,
        code,
        description,
        start: start.format('HH:mm'),
        durationInSeconds
      };

      await createAppointmentGrouping(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getAppointmentGroupings.name);
          handleClose();
          resetForm();
        }
      });
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        title={
          appointmentGroup
            ? t('activityPlanning.editRoute', { route: appointmentGroup.code })
            : t('activityPlanning.addRoute')
        }
        open={open}
        handleClose={() => {
          resetForm();
          handleClose();
        }}
        slots={{
          paper: StyledCustomMediumDialogPaper
        }}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <GridContainer>
            <GridCompact12>
              <ControlledPlanUnitSelector
                name="organizationalUnit"
                control={control}
                variant="outlined"
                fullWidth
                hideGroups
                hideNavigation
                useEmployeePlanunits
                isSingleSelect
                doNotPreselectOnlyOption
                doNotPersist
              />
            </GridCompact12>
            <GridCompact6>
              <Controller
                name="code"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    fullWidth
                    {...field}
                    label={t('activityPlanning.routeNumber')}
                    variant="outlined"
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? null}
                    required
                    slotProps={{
                      htmlInput: {
                        maxLength: 10
                      }
                    }}
                  />
                )}
              />
            </GridCompact6>
            <GridCompact6>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    fullWidth
                    {...field}
                    label={t('activityPlanning.routeName')}
                    variant="outlined"
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? null}
                    required
                  />
                )}
              />
            </GridCompact6>
            <GridCompact4>
              <Controller
                name="start"
                control={control}
                render={({ field: { ref, ...fieldProps }, fieldState }) => {
                  return (
                    <DesktopTimePicker
                      {...fieldProps}
                      ampm={false}
                      inputRef={ref}
                      label={t('startTime')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          size: 'small',
                          error: !!fieldState?.error?.type,
                          helperText: fieldState?.error?.message
                        }
                      }}
                    />
                  );
                }}
              />
            </GridCompact4>
            <GridCompact2>
              <ControlledTextField
                variant="outlined"
                name="hourDuration"
                label={t('hours')}
                control={control}
                slotProps={{
                  input: {
                    inputProps: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }
                  }
                }}
              />
            </GridCompact2>

            <GridCompact2>
              <ControlledTextField
                variant="outlined"
                name="minuteDuration"
                label={t('minutes')}
                control={control}
                slotProps={{
                  input: {
                    inputProps: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }
                  }
                }}
              />
            </GridCompact2>
            <GridCompact4>
              <ControlledTextField
                variant="outlined"
                name="endTime"
                label={t('endTime')}
                control={control}
                disabled
              />
            </GridCompact4>
          </GridContainer>
          <DialogActions>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => {
                handleClose();
                resetForm();
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              color="primary"
            >
              {t('save')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
}
